import { Link } from "react-router-dom";
import { ArrowRight, Compass, HandHeart, ShieldCheck, Sparkle } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import { WHY_US, STATS } from "@/data/site";

const IMG = "https://images.unsplash.com/photo-1539635278303-d4002c07eae3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHBlb3BsZSUyMHRyYXZlbGluZ3xlbnwwfHx8fDE3ODIzNzg2Njl8MA&ixlib=rb-4.1.0&q=85";
const VALUES = [
  { icon: Compass, title: "Curiosity first", desc: "We chase the road less travelled and the stories worth telling." },
  { icon: HandHeart, title: "People over profit", desc: "Every itinerary is built around you, not a margin sheet." },
  { icon: ShieldCheck, title: "Trust & transparency", desc: "Clear pricing, honest advice and support that never sleeps." },
  { icon: Sparkle, title: "Craft in the detail", desc: "The small touches that turn a good trip into an unforgettable one." },
];

export default function About() {
  return (
    <div className="pt-14">
      <section className="max-w-7xl mx-auto px-5 md:px-10">
        <Reveal className="max-w-3xl">
          <p className="font-accent text-xs font-bold uppercase tracking-[0.25em] text-brand-orange mb-4">Our Story</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-medium text-brand-navy leading-[1.05]">
            We believe you should <span className="italic text-brand-teal">travel to live</span>, and live to travel.
          </h1>
        </Reveal>
        <Reveal delay={0.15} className="mt-8 grid md:grid-cols-2 gap-10 items-center">
          <p className="text-lg text-[#5b5980] font-body leading-relaxed">
            Cluetrip Holidays Pvt. Ltd. was born in Ghaziabad from a simple idea — that travel is not a checklist, it’s a way of living.
            Over the years we’ve sent thousands of travellers to the valleys of Uttarakhand, the backwaters of Kerala, the dunes of Dubai
            and the atolls of the Maldives. Every trip is designed by real people who’ve walked the roads themselves.
          </p>
          <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
            <img src={IMG} alt="Cluetrip travellers" loading="lazy" className="h-full w-full object-cover" />
          </div>
        </Reveal>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-y border-brand-sand py-12">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="text-center">
              <p className="font-heading text-4xl md:text-5xl font-semibold text-brand-teal">{s.value}</p>
              <p className="mt-2 text-sm text-[#6b6a8a]">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-8">
        <Reveal className="max-w-2xl mb-12">
          <p className="font-accent text-xs font-bold uppercase tracking-[0.25em] text-brand-orange mb-4">What we stand for</p>
          <h2 className="text-4xl sm:text-5xl font-heading font-medium text-brand-navy leading-tight">Values that shape every journey</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={(i % 4) * 0.08}>
              <div className="h-full rounded-2xl bg-white border border-brand-sand p-8">
                <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
                  <v.icon size={28} weight="duotone" />
                </span>
                <h3 className="mt-6 text-xl font-heading font-medium text-brand-navy">{v.title}</h3>
                <p className="mt-3 text-sm text-[#6b6a8a] leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY US CHAPTERS */}
      <section className="bg-brand-sand py-24 md:py-32 mt-16">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <Reveal className="mb-14">
            <h2 className="text-4xl sm:text-5xl font-heading font-medium text-brand-navy leading-tight">The Cluetrip promise</h2>
          </Reveal>
          <div className="space-y-10">
            {WHY_US.map((w, i) => (
              <Reveal key={w.no} delay={i * 0.05}>
                <div className="flex gap-6 md:gap-10 border-b border-brand-navy/10 pb-8">
                  <span className="font-heading text-3xl md:text-5xl text-brand-teal/50">{w.no}</span>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-heading font-medium text-brand-navy">{w.title}</h3>
                    <p className="mt-3 text-[#5b5980] font-body leading-relaxed max-w-2xl">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-navy py-20 text-center grain relative overflow-hidden">
        <div className="relative z-10 max-w-2xl mx-auto px-5">
          <h2 className="text-3xl sm:text-4xl font-heading font-medium text-white">Ready to write your next chapter?</h2>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-orangedark">
            Start planning <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
